import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Button, Chip, Card, useTheme, Drawer, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, IconButton } from '@mui/material';
import { useEncryptService } from '../../../shared/hooks';
import { Add as AddIcon, Refresh as RefreshIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import AgGridTable from '../../../components/table/AgGridTable';
import AddAgent from './AddAgent';
import AddAgentWizard from './AddAgentWizard';
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared"
import AgentDetailsOverlay from './AgentDetailsOverlay';
import LinkedScansDrawer from './LinkedScansDrawer';
import NewScanDrawer from '../../onPremises/newScanV2/NewScanDrawer';
import AddBoxIcon from '@mui/icons-material/AddBox';
import WhiteTooltip from "../../../components/WhiteTooltip";
import AgentActionMenu from './AgentActionMenu';
import AgentListEmptyState from './AgentListEmptyState';
import EditAgent from './EditAgent';
import UnenrollConfirmationModal from '../../../components/UnenrollConfirmationModal';
import ChangeScheduleModal from '../../onPremises/myScan/ChangeScheduleModal';
import { toast } from 'react-toastify';
import { statusLabelMap, getAgentStatusColor as getStatusColor } from './agentStatusDisplay';
import useRolePermissions from '../../../shared/hooks/useRolePermissions';
import { ROLES_ROUTE } from '../../../shared/constants/constant-data';
import eventEmitter from '../../../components/utils/eventEmitter';


// ==================== TYPES ====================
interface Agent {
  id: string;
  name: string;
  description: string;
  linkedScans: number;
  host_ip?: string;
  version?: string;
  last_activated?: string;
  last_modified: string;
  last_activity?: string;
  status_display: string;
  hasExtendEnrollment?: boolean;
  otp?: string;
  otp_created_at?: string;
  spark_agent_uninstall_cmd?: string;
  spark_agent_resume_cmd?: string;
  meta_data: {
    mac_id: string;
    host_ip: string;
    hostname: string;
    date_installed: string;
  };
}

// ==================== COMPONENT ====================
const AgentTableView: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { hasPermissions } = useRolePermissions();
  const canEditAgent = hasPermissions("create_edit_agents_new") || hasPermissions("create_edit_agent_new");
  const isAgentPath = location.pathname.includes('organization/agent');
  const isDarkMode = theme.palette.mode === 'dark';

  const gridRef = useRef<any>(null);
  const { doEncryptLogin } = useEncryptService();

  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  // ==================== STATE ====================
  const [agentData, setAgentData] = useState<Agent[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isAddAgentOpen, setIsAddAgentOpen] = useState(false);
  const [isAddAgentWizardOpen, setIsAddAgentWizardOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isAgentDetailsOpen, setIsAgentDetailsOpen] = useState(false);
  const [linkedScansAgent, setLinkedScansAgent] = useState<Agent | null>(null);
  const [isLinkedScansOpen, setIsLinkedScansOpen] = useState(false);
  const [isNewScanOpen, setIsNewScanOpen] = useState(false);
  const [newScanAgent, setNewScanAgent] = useState<Agent | null>(null);
  const [isEditAgentOpen, setIsEditAgentOpen] = useState(false);
  const [isUnenrollOpen, setIsUnenrollOpen] = useState(false);
  const [isUnenrollWarningOpen, setIsUnenrollWarningOpen] = useState(false);
  const [isPauseOpen, setIsPauseOpen] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);
  const [isExtendOpen, setIsExtendOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isChangeScheduleOpen, setIsChangeScheduleOpen] = useState(false);
  const [selectedScheduleData, setSelectedScheduleData] = useState<any>(null);
  const [refreshLinkedScansFn, setRefreshLinkedScansFn] = useState<(() => void) | null>(null);
  const addAgentWizardRef = useRef<any>(null);

  const genericUninstallCmd = useMemo(() => {
    const agent = agentData.find((a: any) => a.spark_agent_uninstall_cmd || a.uninstall_cmd || a.spark_uninstall_cmd || a.uninstall_command);
    return agent ? (agent.spark_agent_uninstall_cmd || (agent as any).uninstall_cmd || (agent as any).spark_uninstall_cmd || (agent as any).uninstall_command) : undefined;
  }, [agentData]);

  const fetchAgent = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
      if (response && response.status === 200) {
        setAgentData(response.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }

  }, []);
  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  // ==================== SOCKET: Listen for agent status broadcast events ====================
  useEffect(() => {
    const handleAgentStatusUpdate = (data: any) => {
      console.log('[AgentTableView] agent_status_update received:', data);
      const { agent_id, status, action, agent_name } = data;
      if (!agent_id || !status) return;

      // For agent_installed with 'Active' status, refetch the full list
      // since the agent now has metadata (host, mac, etc.) that wasn't present before
      if (action === 'agent_installed' && status === 'Active') {
        fetchAgent();
        return;
      }

      // For all other status updates (pause/resume/uninstall/installing), update inline
      setAgentData((prev) => {
        const agentExists = prev.some((agent) => agent.id === agent_id);
        if (agentExists) {
          return prev.map((agent) =>
            agent.id === agent_id
              ? { ...agent, status_display: status }
              : agent
          );
        }
        // If agent doesn't exist in the list (e.g. newly created agent installing), refetch
        fetchAgent();
        return prev;
      });
    };

    eventEmitter.on('agent_status_update', handleAgentStatusUpdate);
    return () => {
      eventEmitter.off('agent_status_update', handleAgentStatusUpdate);
    };
  }, [fetchAgent]);


  const openLinkedScans = useCallback((agent: Agent) => {
    setLinkedScansAgent(agent);
    setIsLinkedScansOpen(true);
  }, []);

  const openNewScan = useCallback((agent: Agent) => {
    setNewScanAgent(agent);
    setIsNewScanOpen(true);
  }, []);

  const handleOpenNewScan = useCallback(() => {
    // Close Linked Scans drawer, open New Scan drawer
    setNewScanAgent(linkedScansAgent);
    setIsLinkedScansOpen(false);
    setIsNewScanOpen(true);
  }, [linkedScansAgent]);

  const handleBackToLinkedScans = useCallback(() => {
    // Close New Scan drawer, re-open Linked Scans drawer
    setIsNewScanOpen(false);
    setIsLinkedScansOpen(true);
  }, []);

  const handleCloseNewScan = useCallback(() => {
    setIsNewScanOpen(false);
    setNewScanAgent(null);
  }, []);

  const handleAddAgentSubmit = useCallback((data: any) => {
    console.log('Agent created:', data);
    fetchAgent();
  }, [fetchAgent]);

  const handleEditAgent = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setIsEditAgentOpen(true);
  }, []);

  const handlePauseAgent = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setIsPauseOpen(true);
  }, []);

  const handleResumeAgent = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setIsResumeOpen(true);
  }, []);

  const handleUnenrollAgent = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setIsUnenrollOpen(true);
  }, []);

  const confirmPause = async () => {
    if (!selectedAgent) return;
    setIsActionLoading(true);
    try {
      const endpoint = `${END_POINTS.ADD_AGENT}/${selectedAgent.id}`;
      const response = await axiosInstance.put(endpoint, { is_paused: true });
      if (response.status === 200) {
        toast.success('Agent paused successfully');
        fetchAgent();
        setIsPauseOpen(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to pause agent');
    } finally {
      setIsActionLoading(false);
    }
  };

  const confirmResume = async () => {
    if (!selectedAgent) return;
    setIsActionLoading(true);
    try {
      const endpoint = `${END_POINTS.ADD_AGENT}/${selectedAgent.id}`;
      const response = await axiosInstance.put(endpoint, { is_paused: false });
      if (response.status === 200) {
        toast.success('Agent resumed successfully');
        fetchAgent();
        setIsResumeOpen(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resume agent');
    } finally {
      setIsActionLoading(false);
    }
  };

  const confirmUnenroll = async (password?: string) => {
    if (!selectedAgent) return;
    setIsActionLoading(true);
    try {
      // Password confirmation if required (matching AgentDetails logic)
      if (password) {
        const encryptedPassword = doEncryptLogin(password);
        await axiosInstance.post(END_POINTS.PASSWORD_CONFIRMATION, { password: encryptedPassword });
      }
      const endpoint = `${END_POINTS.ADD_AGENT}/${selectedAgent.id}`;
      const response = await axiosInstance.put(endpoint, { is_unenrolled: true });
      if (response.status === 200) {
        toast.success('Agent unenrolled successfully');
        fetchAgent();
        setIsUnenrollOpen(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unenroll agent');
    } finally {
      setIsActionLoading(false);
    }
  };
  const handleExtendEnrollment = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setIsExtendOpen(true);
  }, []);

  const confirmExtend = async () => {
    if (!selectedAgent) return;
    setIsActionLoading(true);
    try {
      const payload = {
        otp: selectedAgent.otp,
        otp_created_at: selectedAgent.otp_created_at
      };
      const response = await axiosInstance.post(`${END_POINTS.EXTEND_ENROLLMENT}/${selectedAgent.id}/extend-enrollment`, payload);
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Enrollment extended successfully");
        fetchAgent();
        setIsExtendOpen(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to extend enrollment period");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEditSchedule = useCallback((data: any, refreshFn?: () => void) => {
    setSelectedScheduleData(data);
    setIsChangeScheduleOpen(true);
    if (refreshFn) {
      setRefreshLinkedScansFn(() => refreshFn);
    } else {
      setRefreshLinkedScansFn(null);
    }
  }, []);

  // ==================== COLUMN DEFINITIONS ====================
  const columnDefs: ColDef[] = useMemo(
    () => [
      // {
      //   headerCheckboxSelection: true,
      //   checkboxSelection: true,
      //   minWidth: 50,
      //   maxWidth: 50,
      //   // pinned: 'left',
      //   lockPosition: true,
      //   suppressMenu: true,
      // },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        filter: false,
        sortable: false,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value} arrow>
            <span style={{ color: isDarkMode ? '#9CA3AF' : '#666' }}>
              {params.value}
            </span>
          </WhiteTooltip>
        )
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 200,
        filter: true,
        sortable: true,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value} arrow>
            <span style={{ color: isDarkMode ? '#9CA3AF' : '#666' }}>
              {params.value}
            </span>
          </WhiteTooltip>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        minWidth: 200,
        filter: true,
        sortable: true,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value} arrow>
            <span style={{ color: isDarkMode ? '#9CA3AF' : '#666' }}>
              {params.value}
            </span>
          </WhiteTooltip>
        )
      },
      {
        field: 'linked_scans_count',
        headerName: 'Linked Scans',
        minWidth: 150,
        filter: true,
        sortable: true,
        cellRenderer: (params: ICellRendererParams) => (
          <span
            data-prevent-row-click="true"
            style={{ cursor: 'pointer', color: '#3A2ADA', textDecoration: 'underline' }}
            onClick={() => openLinkedScans(params.data)}
          >
            <span style={{ color: '#5F62E7' }}>{params.value}</span>
          </span>
        ),
      },
      {
        field: 'meta_data.hostname',
        headerName: 'Host',
        minWidth: 150,
        filter: true,
        sortable: true,
        cellRenderer: (params: ICellRendererParams) => (<WhiteTooltip title={params.value} arrow>
          <span style={{ color: isDarkMode ? '#9CA3AF' : '#666' }}>{params.value || '-'}</span>
        </WhiteTooltip>
        ),
      },
      {
        field: 'last_activity',
        headerName: 'Last Activity',
        minWidth: 250,
        filter: true,
        sortable: true,
        cellRenderer: (params: ICellRendererParams) => {
          const hasExtend = params.data.hasExtendEnrollment && params.value?.includes('Enroll Time');
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ color: isDarkMode ? '#9CA3AF' : '#666' }}>
                {params.value ? new Date(params.value).toLocaleString() : 'N/A'}
              </span>
              {(params.data.status_display === 'Pending' || params.data.status_display === 'Inactive') && (
                canEditAgent ? (
                  <WhiteTooltip title="Extend Enrollment Period" arrow>
                    <AddBoxIcon
                      sx={{
                        fontSize: 18,
                        color: '#4640DE',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExtendEnrollment(params.data);
                      }}
                      data-prevent-row-click="true"
                    />
                  </WhiteTooltip>
                ) : (
                  <AddBoxIcon
                    sx={{
                      fontSize: 18,
                      color: isDarkMode ? '#ACA8FA' : '#C7C4FF', // light disabled violet type
                      opacity: isDarkMode ? 0.5 : 1,
                      cursor: 'default',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    data-prevent-row-click="true"
                  />
                )
              )}
              {hasExtend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RefreshIcon sx={{ fontSize: 16, color: '#3A2ADA' }} />
                  <span style={{ fontSize: 12, color: '#3A2ADA' }}>📱</span>
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        field: 'status_display',
        headerName: 'Status',
        minWidth: 200,
        filter: true,
        sortable: false,
        cellRenderer: (params: ICellRendererParams) => {
          const statusStyle = getStatusColor(params.value, isDarkMode);
          return (
            <Chip
              label={statusLabelMap[params.value] || params.value}
              size="small"
              sx={{
                ...statusStyle,
                fontSize: '13px',
                height: '20px',
                width: 'auto',
                fontWeight: 600,
                borderRadius: '4px',
                px: 0.5,
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        minWidth: 170,
        maxWidth: 170,
        // pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermissions(ROLES_ROUTE.NEW_SCAN) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                data-prevent-row-click="true"
                disabled={params.data.status_display !== 'Active'}
                onClick={(e) => {
                  e.stopPropagation();
                  openNewScan(params.data);
                }}

                sx={{
                  fontSize: '11px',
                  px: 1,
                  minWidth: '90px',
                  height: '28px',
                  textTransform: 'none',
                  borderColor: params.data.status_display !== 'Active' ? '#E0E0E0' : '#4640DE',
                  color: params.data.status_display !== 'Active' ? '#9E9E9E' : '#4640DE',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: params.data.status_display !== 'Active' ? '#E0E0E0' : '#3730CC',
                    bgcolor: params.data.status_display !== 'Active' ? 'transparent' : '#4640DE',
                    color: params.data.status_display !== 'Active' ? '#9E9E9E' : '#fff',
                    transform: params.data.status_display !== 'Active' ? 'none' : 'translateY(-1px)',
                    boxShadow: params.data.status_display !== 'Active' ? 'none' : '0 2px 8px rgba(70, 64, 222, 0.25)',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E0E0E0',
                    color: '#9E9E9E',
                  }
                }}
              >
                Add Scan
              </Button>
            )}
            <AgentActionMenu
              params={params}
              onEdit={handleEditAgent}
              onPause={handlePauseAgent}
              onResume={handleResumeAgent}
              onUnenroll={handleUnenrollAgent}
              onLinkedScans={openLinkedScans}
            />
          </Box>
        ),
      },
    ],
    [isDarkMode, openLinkedScans, openNewScan, handleEditAgent, handlePauseAgent, handleResumeAgent, handleUnenrollAgent, handleExtendEnrollment, canEditAgent]
  );

  const openAddAgentWizard = useCallback(() => {
    setSelectedAgent(null);
    setIsAddAgentWizardOpen(true);
  }, []);

  const canCreateAgent = hasPermissions("create_edit_agents_new") || hasPermissions("create_edit_agent_new");

  const headerActions = canCreateAgent ? (
    <Button
      variant="outlined"
      // startIcon={<AddIcon sx={{ fontSize: 16 }} />}
      onClick={openAddAgentWizard}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '12px',
        padding: '6px 16px',
        borderColor: '#4640DE',
        color: '#4640DE',
        borderRadius: '6px',
        borderWidth: '1.5px',
        height: '28px',
        // width: '100px',
        minWidth: 'auto',
        letterSpacing: '0.3px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#3730CC',
          borderWidth: '1.5px',
          bgcolor: '#4640DE',
          color: '#fff',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(70, 64, 222, 0.25)',
        },
        '&:active': {
          transform: 'translateY(0px)',
        },
      }}
    >
      Create Agent
    </Button>
  ) : null;

  const showEmptyState = !isLoading && agentData.length === 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "calc(100vh - 103px)",
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {showEmptyState ? (
        <AgentListEmptyState onAddAgent={openAddAgentWizard} />
      ) : (
        <AgGridTable
          columnDefs={columnDefs}
          data={agentData}
          styles={agGridInlineStyles}
          isLoading={isLoading}

          height={"calc(100vh - 105px)"}
          showSearch
          headerActions={headerActions}
          gridRef={gridRef}
          onRowClick={(rowData: any) => {
            setSelectedAgent(rowData);
            if (rowData.status_display === 'Pending') {
              setIsAddAgentWizardOpen(true);
            } else {
              setIsAgentDetailsOpen(true);
            }
          }}
          gridOptions={{
            rowSelection: 'multiple',
            suppressRowClickSelection: true,
          }}
        />
      )}

      {/* Modals */}
      {isAddAgentOpen && (
        <AddAgent
          isOpen={isAddAgentOpen}
          onClose={() => setIsAddAgentOpen(false)}
          addAgentList={agentData}
          onSubmit={handleAddAgentSubmit}
        />
      )}
      {/* Add Agent Wizard Drawer */}
      <Drawer
        anchor="right"
        open={isAddAgentWizardOpen}
        onClose={() => addAgentWizardRef.current?.handleClose()}
        PaperProps={{
          sx: {
            width: { xs: '100%', },
            maxWidth: '45vw',
            p: 0,
          },
        }}
      >
        {isAddAgentWizardOpen && (
          <AddAgentWizard
            ref={addAgentWizardRef}
            onClose={() => setIsAddAgentWizardOpen(false)}
            onSubmit={handleAddAgentSubmit}
            selectedAgent={selectedAgent}
          />
        )}
      </Drawer>

      {/* Agent Details Drawer */}
      <Drawer
        anchor="right"
        open={isAgentDetailsOpen}
        onClose={() => setIsAgentDetailsOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 750 }, maxWidth: '80vw', p: 0 } }}
      >
        {selectedAgent && (
          <Box sx={{ height: '100%', p: 0 }}>
            {/* AgentDetails component expects updateAgent, selectedAgent, isDarkMode */}
            <AgentDetailsOverlay
              selectedAgent={selectedAgent}
              onClose={() => setIsAgentDetailsOpen(false)}
              onOpenLinkedScans={openLinkedScans}
            />
          </Box>
        )}
      </Drawer>

      {/* Linked scans drawer */}
      <Drawer
        anchor="right"
        open={isLinkedScansOpen}
        onClose={() => setIsLinkedScansOpen(false)}
        PaperProps={{ sx: { width: '50%', p: 0 } }}
      >
        {linkedScansAgent && (
          <LinkedScansDrawer
            agent={linkedScansAgent}
            onClose={() => setIsLinkedScansOpen(false)}
            onOpenNewScan={handleOpenNewScan}
            onEditSchedule={handleEditSchedule}
          />
        )}
      </Drawer>

      {/* New Scan drawer */}
      <NewScanDrawer
        open={isNewScanOpen}
        initialScanType="onprem-discovery-posture"
        initialAgentId={newScanAgent?.id}
        onClose={handleCloseNewScan}
        onBack={handleBackToLinkedScans}
        onConnectSuccess={fetchAgent}
      />

      {/* Edit Agent Drawer */}
      {isEditAgentOpen && selectedAgent && (
        <EditAgent
          isOpen={isEditAgentOpen}
          agentData={selectedAgent}
          onClose={() => setIsEditAgentOpen(false)}
          onSubmit={(data) => {
            const endpoint = `${END_POINTS.ADD_AGENT}/${selectedAgent.id}`;
            axiosInstance.put(endpoint, data).then(() => {
              toast.success('Agent updated successfully');
              fetchAgent();
              setIsEditAgentOpen(false);
            }).catch((err) => {
              toast.error(err.response?.data?.message || 'Failed to update agent');
            });
          }}
        />
      )}

      {/* Unenroll Confirmation Modal */}
      {isUnenrollOpen && selectedAgent && (
        <UnenrollConfirmationModal
          open={isUnenrollOpen}
          onClose={() => setIsUnenrollOpen(false)}
          onConfirm={confirmUnenroll}
          agentName={selectedAgent.name}
          agentHost={selectedAgent.meta_data?.hostname || selectedAgent.host_ip || selectedAgent.meta_data?.host_ip}
          showPassword={true}
          isLoading={isActionLoading}
          agentId={selectedAgent.id}
          uninstallCommand={selectedAgent.spark_agent_uninstall_cmd || (selectedAgent as any).uninstall_cmd || (selectedAgent as any).spark_uninstall_cmd || (selectedAgent as any).uninstall_command || genericUninstallCmd}
        />
      )}

      {/* Pause Confirmation Dialog */}
      <Dialog
        open={isPauseOpen}
        onClose={() => setIsPauseOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '420px',
            maxWidth: '90vw',
            borderRadius: '10px',
            padding: '20px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            backgroundColor: isDarkMode ? '#1E2330' : '#FFFFFF',
            boxShadow: isDarkMode
              ? '0px 8px 24px rgba(0, 0, 0, 0.5)'
              : '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', padding: '0 0 8px 0', color: isDarkMode ? '#FFFFFF' : '#111827' }}>
          Pause Agent
        </DialogTitle>
        <DialogContent sx={{ padding: '0' }}>
          <DialogContentText sx={{ fontSize: '14px', color: isDarkMode ? '#D1D5DB' : '#374151', lineHeight: '22px' }}>
            Are you sure you want to pause <strong>"{selectedAgent?.name}"</strong>? You won't be able to connect using this tunnel. You can resume it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '14px 0 0 0', gap: '10px', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setIsPauseOpen(false)}
            disabled={isActionLoading}
            sx={{
              minWidth: '100px', height: '34px', borderRadius: '6px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              borderColor: isDarkMode ? '#4A5568' : '#d1d5db', color: isDarkMode ? '#D1D5DB' : '#374151',
              '&:hover': { borderColor: isDarkMode ? '#718096' : '#9ca3af', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPause}
            disabled={isActionLoading}
            sx={{
              minWidth: '100px', height: '34px', borderRadius: '6px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              bgcolor: '#F7941F', color: '#FFFFFF', boxShadow: 'none',
              '&:hover': { bgcolor: '#e0861a', boxShadow: 'none' },
              '&:disabled': { backgroundColor: isDarkMode ? '#3D3D4E' : '#E5E7EB', color: isDarkMode ? '#7D7D8E' : '#9CA3AF' },
            }}
          >
            {isActionLoading ? 'Pausing...' : 'Pause'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Confirmation Dialog */}
      <Dialog open={isResumeOpen} onClose={() => setIsResumeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resume Agent "{selectedAgent?.name}"</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            To resume the agent, please run the following command on your host:
          </DialogContentText>
          <Box
            sx={{
              bgcolor: isDarkMode ? '#111827' : '#F5F5FF',
              border: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
              p: 2,
              borderRadius: '10px',
              fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
              fontSize: '13px',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: isDarkMode ? '#d1d5db' : '#374151',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>{selectedAgent?.spark_agent_resume_cmd || '—'}</Box>
            {selectedAgent?.spark_agent_resume_cmd && (
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(selectedAgent.spark_agent_resume_cmd || '');
                  toast.success('Command copied to clipboard');
                }}
                sx={{
                  bgcolor: isDarkMode ? '#1f2937' : '#fff',
                  border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
                  borderRadius: '6px',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: isDarkMode ? '#374151' : '#f3f4f6',
                    borderColor: '#4640DE',
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 15, color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
              </IconButton>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setIsResumeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Extend Enrollment Confirmation Dialog */}
      <Dialog
        open={isExtendOpen}
        onClose={() => setIsExtendOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '420px',
            maxWidth: '90vw',
            borderRadius: '10px',
            padding: '20px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            backgroundColor: isDarkMode ? '#1E2330' : '#FFFFFF',
            boxShadow: isDarkMode
              ? '0px 8px 24px rgba(0, 0, 0, 0.5)'
              : '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogContent sx={{ padding: '0' }}>
          <Typography
            component="div"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '15px',
              lineHeight: 1.6,
              color: isDarkMode ? '#E5E7EB' : '#111827',
              py: 0.5,
            }}
          >
            Are you sure, You want to extend
            

            the

            agent
            expiration time by
            

            72-hours?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '14px 0 0 0', gap: '10px', display: "flex", justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={() => setIsExtendOpen(false)}
            disabled={isActionLoading}
            fullWidth
            sx={{
              height: '34px', borderRadius: '6px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              borderColor: isDarkMode ? '#4A5568' : '#d1d5db', color: isDarkMode ? '#D1D5DB' : '#374151',
              '&:hover': { borderColor: isDarkMode ? '#718096' : '#9ca3af', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmExtend}
            disabled={isActionLoading}
            fullWidth
            sx={{
              height: '34px', borderRadius: '6px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              bgcolor: '#4640DE', color: '#FFFFFF', boxShadow: 'none',
              '&:hover': { bgcolor: '#3730CC', boxShadow: 'none' },
              '&:disabled': { backgroundColor: isDarkMode ? '#3D3D4E' : '#E5E7EB', color: isDarkMode ? '#7D7D8E' : '#9CA3AF' },
            }}
          >
            {isActionLoading ? 'Extending...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>

      {isChangeScheduleOpen && selectedScheduleData && (
        <ChangeScheduleModal
          open={isChangeScheduleOpen}
          onClose={() => {
            setIsChangeScheduleOpen(false);
            setSelectedScheduleData(null);
          }}
          initialValues={{
            schedule_id: selectedScheduleData.schedule?.schedule_id || selectedScheduleData.schedule_id,
            time: selectedScheduleData.schedule?.time,
            frequency_id: selectedScheduleData.schedule?.frequency,
            date: selectedScheduleData.schedule?.date,
            day: selectedScheduleData.schedule?.day,
          }}
          scheduleId={selectedScheduleData.schedule?.schedule_id || selectedScheduleData.schedule_id}
          scheduleName={selectedScheduleData.schedule_name || selectedScheduleData.scan_name}
          module="onpremises"
          type={selectedScheduleData.scan_type}
          fetchScanData={() => {
            if (refreshLinkedScansFn) {
              refreshLinkedScansFn();
            }
          }}
          refetch={() => {
            if (refreshLinkedScansFn) {
              refreshLinkedScansFn();
            }
          }}
          isScansV2ScheduleFlow={true}
        />
      )}
    </Card>
  );
};

export default AgentTableView;

 